import { InternalAxiosRequestConfig } from 'axios';
import { useEffect, useState } from 'react';
import { DEFAULT_USER_LANGUAGE } from '../constants/common';
import { getUserLanguagePreferenceKey } from '../session/session.helpers';
import { StorageService } from '../utility/StorageService';
import { DISTRIBUTION_CHANNEL } from './api.constants';
import { ApiInstanceConfiguration } from './api.interfaces';
import {
  JSON_LONG_AS_STRING_PARAM,
  LANGUAGE_URL_PARAM,
  RESTRICTED_HEADERS_FOR_METADATA,
  SYSTEM_ID_URL_PARAM
} from './metadata.api.constants';

/**
 * Storage service instance for metadata language override.
 */
const metadataLanguageStorage = new StorageService<string>({
  useSessionStorage: true,
  namespace: 'MetadataLanguage'
});

const METADATA_LANGUAGE_OVERRIDE_KEY = 'globalOverride';

/**
 * Sets the global language override for all metadata requests.
 *
 * @param language - The language code to use for metadata requests, or undefined to clear the override
 */
export function setGlobalMetadataLanguageOverride(language?: string) {
  if (language) {
    metadataLanguageStorage.save(METADATA_LANGUAGE_OVERRIDE_KEY, language);
  } else {
    metadataLanguageStorage.remove(METADATA_LANGUAGE_OVERRIDE_KEY);
  }
}

/**
 * Gets the current global language override value.
 *
 * @returns The language code currently set as global override, or undefined if not set
 */
export function getGlobalMetadataLanguageOverride(): string | undefined {
  return (
    metadataLanguageStorage.retrieve(METADATA_LANGUAGE_OVERRIDE_KEY) ||
    undefined
  );
}

/**
 * React hook that returns the current global metadata language override
 * and automatically re-renders when it changes.
 *
 * @returns The current global language override, or undefined if not set
 */
export function useGlobalMetadataLanguageOverride(): string | undefined {
  const [language, setLanguage] = useState<string | undefined>();

  useEffect(() => {
    setLanguage(getGlobalMetadataLanguageOverride());

    const handleStorageChange = () => {
      setLanguage(getGlobalMetadataLanguageOverride());
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  return language;
}

/**
 * Computes the effective language for metadata calls using hierarchical resolution.
 *
 * Language resolution follows this priority order:
 * 1. Global override
 * 2. User language preferences
 * 3. Business unit default language
 * 4. System default language
 *
 * @param sessionConfig - Session configuration containing user preferences and business unit settings
 * @returns The resolved language code that will be used for metadata requests
 */
export function getEffectiveMetadataLanguage(
  sessionConfig: ApiInstanceConfiguration
): string {
  const globalOverride = getGlobalMetadataLanguageOverride();

  return (
    globalOverride ||
    sessionConfig.preferences[
      getUserLanguagePreferenceKey(sessionConfig.systemId)
    ] ||
    sessionConfig.buLanguage ||
    DEFAULT_USER_LANGUAGE
  );
}

/**
 * Creates an Axios request interceptor for metadata API calls.
 *
 * This interceptor removes restricted headers and applies URL parameter transformations
 * required for metadata service communication.
 *
 * @param sessionConfig - Session configuration for URL parameter generation
 * @returns Configured request interceptor function
 */
export function axiosMetadataRequestInterceptor(
  sessionConfig: ApiInstanceConfiguration
): (arg0: InternalAxiosRequestConfig) => InternalAxiosRequestConfig {
  return function interceptRequestHandler(requestConfig) {
    RESTRICTED_HEADERS_FOR_METADATA.forEach((restrictedHeader) => {
      delete requestConfig.headers?.[restrictedHeader];
    });

    return setUrlParams(requestConfig, sessionConfig);
  };
}

/**
 * Handles metadata request errors by propagating them through the promise chain.
 *
 * @param error - The error that occurred during the request
 * @returns Rejected promise with the original error
 */
export function interceptRequestError(error: Error): Promise<any> {
  return Promise.reject(error);
}

/**
 * Transforms request URL by appending required metadata service parameters.
 *
 * This function constructs the complete URL path including language, system ID,
 * distribution channel, and optional JSON formatting parameters required by
 * the metadata service API.
 *
 * @param requestConfig - Axios request configuration to modify
 * @param sessionConfig - Session configuration containing system and language settings
 * @returns Modified request configuration with updated URL
 */
export function setUrlParams(
  requestConfig: InternalAxiosRequestConfig,
  sessionConfig: ApiInstanceConfiguration
) {
  const jsonLongAsString = requestConfig?.url?.includes(
    `/${JSON_LONG_AS_STRING_PARAM}`
  )
    ? `/${JSON_LONG_AS_STRING_PARAM}`
    : '';

  const language = getEffectiveMetadataLanguage(sessionConfig);

  const headerItems = `/${LANGUAGE_URL_PARAM}/${language}/${SYSTEM_ID_URL_PARAM}/${sessionConfig.systemId}/${DISTRIBUTION_CHANNEL.URL_PARAM}/${DISTRIBUTION_CHANNEL.ID}${jsonLongAsString}`;
  const urlParts = requestConfig?.url?.split('/') || [];

  // Reorganize URL structure to place ID parameter at the end
  if (urlParts.length === 4) {
    requestConfig.url = `/${urlParts[1]}${headerItems}/${urlParts[2]}/${urlParts[3]}`;
  } else {
    requestConfig.url = `${requestConfig.url}${headerItems}`;
  }

  return requestConfig;
}
